import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import SEOHead from '../components/SEOHead';
import useDocumentTitle from '../hooks/useDocumentTitle';
import QuizTypeEngagementLineChart from '../components/admin/QuizTypeEngagementLineChart';

interface EngagementSummaryMetrics {
  totalQuizzes: number;
  quizzesLast7Days: number;
  quizzesLast30Days: number;
  avgScoreAllTime: number;
  avgScoreLast30Days: number;
  avgCompletionTimeAllTime: number;
  avgCompletionTimeLast30Days: number;
  quizzesSelectedDays?: number;
  avgScoreSelectedDays?: number;
  avgCompletionTimeSelectedDays?: number;
}

interface CompetitiveQuizHistoryQuestion {
  questionId: string;
  questionText: string;
  dateAsked: string;
  topic: string | null;
  subtopic: string;
  difficulty: string;
  percentCorrect: number;
  averageTimeSpent: number;
  isNiche: boolean;
}

interface CompetitiveQuizHistoryEntry {
  date: string;
  participantCount: number;
  averageTimeTaken: number;
  averagePercentCorrect: number;
  questions: CompetitiveQuizHistoryQuestion[];
}

const AdminCompetitive: React.FC = () => {
  useDocumentTitle('Cognidaily | Competitive Admin');

  const [days, setDays] = useState<7 | 30 | 90>(30);
  const [includeBots, setIncludeBots] = useState(false);
  const [summary, setSummary] = useState<EngagementSummaryMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<CompetitiveQuizHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get<{ success: boolean; data: EngagementSummaryMetrics }>(
          '/api/admin/metrics/engagement/summary-by-type',
          { params: { group: 'competitive', includeBots, days } }
        );
        if (!res.data.success) throw new Error('Failed');
        if (!cancelled) setSummary(res.data.data);
      } catch (err: any) {
        console.error('[AdminCompetitive] Failed to load summary', err);
        if (!cancelled) {
          setError(err?.response?.data?.message || 'Failed to load competitive metrics.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [includeBots, days]);

  useEffect(() => {
    let cancelled = false;
    const loadHistory = async () => {
      try {
        setHistoryLoading(true);
        setHistoryError(null);
        const res = await axios.get<{ success: boolean; data: CompetitiveQuizHistoryEntry[] }>(
          '/api/admin/metrics/competitive/history',
          { params: { includeBots, limit: 100 } }
        );
        if (!res.data.success) throw new Error('Failed');
        if (!cancelled) setHistory(res.data.data);
      } catch (err: any) {
        console.error('[AdminCompetitive] Failed to load history', err);
        if (!cancelled) {
          setHistoryError(err?.response?.data?.message || 'Failed to load competitive quiz history.');
        }
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };
    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [includeBots]);

  const toggleExpanded = (date: string) => {
    setExpandedDates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="admin-page">
      <SEOHead
        title="Admin | Quizzes"
        description="Quiz usage metrics and history."
      />
      <header className="admin-page__header">
        <h1 className="admin-page__title">Quizzes</h1>
        <p className="admin-page__subtitle">
          Daily quiz metrics and history for all 5 topics.{' '}
          <Link to="/admin/competitive-upcoming" className="link">Manage upcoming sets →</Link>
        </p>
      </header>

      <div className="admin-page__filters">
        <label className="admin-checkbox">
          <input
            type="checkbox"
            checked={includeBots}
            onChange={(e) => setIncludeBots(e.target.checked)}
          />
          Include bots
        </label>
        <div className="admin-page__presets">
          {([7, 30, 90] as const).map((d) => (
            <button
              key={d}
              type="button"
              className={`btn btn-ghost btn-xs${days === d ? ' btn-ghost--active' : ''}`}
              onClick={() => setDays(d)}
            >
              {d} days
            </button>
          ))}
        </div>
      </div>

      {loading && !summary ? (
        <div className="admin-loading">Loading…</div>
      ) : error ? (
        <div className="admin-error">
          <strong>Error:</strong> {error}
        </div>
      ) : (
        <>
          <section className="admin-grid">
              <article className="admin-card">
                <div className="admin-card__label">Total quizzes</div>
                <div className="admin-card__value">{summary?.totalQuizzes ?? 0}</div>
                <div className="admin-card__meta">
                  {summary?.quizzesLast7Days ?? 0} (7d) • {summary?.quizzesLast30Days ?? 0} (30d)
                </div>
              </article>
              <article className="admin-card">
                <div className="admin-card__label">Avg score</div>
                <div className="admin-card__value">
                  {(summary?.avgScoreSelectedDays ?? summary?.avgScoreLast30Days ?? 0).toFixed(1)}
                </div>
                <div className="admin-card__meta">Last {days} days</div>
              </article>
              <article className="admin-card">
                <div className="admin-card__label">Avg time</div>
                <div className="admin-card__value">
                  {Math.round((summary?.avgCompletionTimeSelectedDays ?? summary?.avgCompletionTimeLast30Days ?? 0) / 1000)}s
                </div>
                <div className="admin-card__meta">Last {days} days</div>
              </article>
            </section>

            <section className="admin-section">
              <h2 className="admin-section__title">Engagement</h2>
              <p className="admin-section__subtitle">Daily quiz volume.</p>
              <article className="admin-usage-card">
                <QuizTypeEngagementLineChart
                  days={days}
                  includeBots={includeBots}
                  seriesKey="mainQuizzes"
                  label="Quizzes"
                  stroke="#6366f1"
                />
              </article>
            </section>

            <section className="admin-section">
              <h2 className="admin-section__title">Quiz history</h2>
              <p className="admin-section__subtitle">Completed quizzes with question details.</p>

              {historyLoading ? (
                <div className="admin-loading">Loading…</div>
              ) : historyError ? (
                <div className="admin-error">{historyError}</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="admin-table" style={{ minWidth: 480 }}>
                    <thead>
                      <tr>
                        <th style={{ width: '3rem' }}></th>
                        <th>Date</th>
                        <th>Participants</th>
                        <th>Avg Time</th>
                        <th>Avg % Correct</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((entry) => {
                        const isExpanded = expandedDates.has(entry.date);
                        return (
                          <React.Fragment key={entry.date}>
                            <tr>
                              <td>
                                <button
                                  type="button"
                                  className="admin-expand-btn"
                                  onClick={() => toggleExpanded(entry.date)}
                                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                >
                                  <svg
                                    className={`admin-expand-icon ${isExpanded ? 'admin-expand-icon--open' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              </td>
                              <td>{formatDate(entry.date)}</td>
                              <td>{entry.participantCount}</td>
                              <td>{Math.round(entry.averageTimeTaken / 1000)}s</td>
                              <td>{entry.averagePercentCorrect.toFixed(1)}%</td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={5} className="admin-table-detail">
                                  <h3>Questions ({entry.questions.length})</h3>
                                  <div>
                                    {entry.questions.map((question) => (
                                      <div key={question.questionId} className="admin-question-item">
                                        <div className="admin-question-item__text">{question.questionText}</div>
                                        <div className="admin-question-item__meta">
                                          <span className={`admin-badge admin-badge--${question.difficulty}`}>{question.difficulty}</span>
                                          {question.isNiche && <span className="admin-badge admin-badge--niche">Niche</span>}
                                          {question.topic && <span> • {question.topic}</span>}
                                          <span> • {question.subtopic}</span>
                                        </div>
                                        <div className="admin-question-item__stats">
                                          <span>{question.percentCorrect.toFixed(1)}% correct</span>
                                          <span>{Math.round(question.averageTimeSpent / 1000)}s avg</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                      {history.length === 0 && (
                        <tr>
                          <td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted, #6b7280)' }}>
                            No quiz history found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
    </div>
  );
};

export default AdminCompetitive;
